import type { FC } from 'react'
import { useEffect, useState, useContext } from 'react'

import Loading from '@/components/Loading'
import type { OverrideOutcomeFieldConfig } from '@/components/OverrideOutcomeFields/OverrideOutcomeFields'
import OverrideOutcomeFields, {
  OverrideHeader,
} from '@/components/OverrideOutcomeFields/OverrideOutcomeFields'
import ctx from '@/context/AppContext'
import { useApplicationOutcomeQuery, useOverrideOutcomeMutation } from '@/lib/apollo'
import type { IdentityOutcome, Outcome } from '@/lib/apollo/model/outcome'
import {
  OutcomeSectionName,
  IdentityOutcomePropertyName,
} from '@/lib/apollo/model/outcome'
import { OutcomeStatus } from '@/types/Outcome'
import type { OutcomeOverrideSection } from '@/types/OutcomeOverride'
import { mapToGraphQLOverrides, overallSectionOverride } from '@/utils/outcomeOverrides'

const updatePropertyOutcomeStatus = (
  outcomeSection: IdentityOutcome,
  propertyName: IdentityOutcomePropertyName,
  outcomeStatus: OutcomeStatus,
): IdentityOutcome => ({
  ...outcomeSection,
  properties: outcomeSection.properties.map(p =>
    p.name === propertyName
      ? {
          ...p,
          status: outcomeStatus,
        }
      : p,
  ),
})

const getPropertyStatusFromOutcome = (
  outcomeSection: IdentityOutcome,
  propertyName: IdentityOutcomePropertyName,
) => outcomeSection.properties.find(({ name }) => name === propertyName)?.status

const fieldConfig: OverrideOutcomeFieldConfig<IdentityOutcome>[] = [
  {
    label: <strong>Identity Section</strong>,
    getStatus: outcomeSection => outcomeSection.status,
    applyOutcomeStatus: (outcomeSection, outcomeStatus) => ({
      ...outcomeSection,
      status: outcomeStatus,
    }),
    options: overallSectionOverride,
  },
  {
    label: 'Facial match',
    getStatus: outcomeSection =>
      getPropertyStatusFromOutcome(
        outcomeSection,
        IdentityOutcomePropertyName.facialMatch,
      ),
    applyOutcomeStatus: (outcomeSection, outcomeStatus) =>
      updatePropertyOutcomeStatus(
        outcomeSection,
        IdentityOutcomePropertyName.facialMatch,
        outcomeStatus,
      ),
  },
]

const shouldBeOverridden = (status?: OutcomeStatus): boolean => {
  if (!status) return false

  return [OutcomeStatus.fail, OutcomeStatus.accepted].includes(status)
}

const toOutcomeOverrides = (outcome: IdentityOutcome): OutcomeOverrideSection[] => {
  const facialMatchStatus = getPropertyStatusFromOutcome(
    outcome,
    IdentityOutcomePropertyName.facialMatch,
  )

  return [
    {
      name: OutcomeSectionName.identity,
      status: outcome.status,
      properties: [
        // Override facialMatch status
        ...(shouldBeOverridden(facialMatchStatus)
          ? [
              {
                name: IdentityOutcomePropertyName.facialMatch,
                status: facialMatchStatus,
              },
            ]
          : []),
      ],
    } as IdentityOutcome,
  ]
}

interface Props {
  customerId: string
  applicationId: string
  subjectId: string
  subjectOutcome?: Outcome
}

const OverrideOutcomes: FC<Props> = ({
  customerId,
  applicationId,
  subjectId,
  subjectOutcome,
}) => {
  const [outcome, setOutcome] = useState<IdentityOutcome | undefined>(undefined)
  const [isSuccess, setIsSuccess] = useState<boolean>(false)
  const [submitError, setSubmitError] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [saveOutcomeOverrides] = useOverrideOutcomeMutation()

  const { personId } = useContext(ctx)

  const userId = personId ?? ''

  const { loading, error, data } = useApplicationOutcomeQuery({
    variables: {
      customerId,
      applicationId,
      subjectId,
      isReport: false,
    },
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    const outcome = data?.getDashboardApplication.outcome ?? undefined

    const identitySectionOutcome = outcome?.sections?.find(
      ({ name }) => name === OutcomeSectionName.identity,
    ) as IdentityOutcome

    setOutcome(identitySectionOutcome)
  }, [data])

  const handleSubmit = async (reason: string) => {
    setIsSuccess(false)
    setSubmitError(false)
    setIsLoading(true)

    if (outcome) {
      const outcomeOverrides = mapToGraphQLOverrides(toOutcomeOverrides(outcome))

      saveOutcomeOverrides({
        variables: {
          customerId,
          applicationId,
          subjectId,
          outcomeOverrides,
          userId,
          reason,
        },
      })
        .then(a => {
          if (a.data?.overrideOutcome === 'Successfully overrode outcome') {
            setIsSuccess(true)
          }
        })
        .catch(() => {
          setSubmitError(true)
        })
        .finally(() => setIsLoading(false))
    }
  }

  return (
    <>
      <OverrideHeader>Review subsections</OverrideHeader>
      {outcome === undefined ? (
        <>There is nothing to review.</>
      ) : error !== undefined ? (
        <>An unexpected error occurred</>
      ) : loading ? (
        <Loading />
      ) : (
        <OverrideOutcomeFields<IdentityOutcome>
          subjectOutcome={subjectOutcome}
          fieldConfig={fieldConfig}
          outcomeSection={outcome}
          onChangeOutcomeSection={setOutcome}
          handleSubmit={handleSubmit}
          isError={submitError}
          isLoading={isLoading}
          isSuccess={isSuccess}
        />
      )}
    </>
  )
}

export default OverrideOutcomes
